import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Plus, Trash2, Eye, Upload, X, CalendarIcon, FileText,
  ChevronRight, Package, CheckCircle2, RotateCcw, AlertTriangle, Pencil, ChevronDown, Printer, Maximize2, Minimize2, Copy
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import FilterBar, { useFilters } from "@/components/FilterBar";

const statusLabels = { pendente: "Pendente", em_andamento: "Em Andamento", concluido: "Concluido", cancelado: "Cancelado" };
const statusColors = { pendente: "bg-slate-100 text-slate-700", em_andamento: "bg-blue-100 text-blue-800", concluido: "bg-green-100 text-green-800", cancelado: "bg-gray-100 text-gray-500" };
const priorityLabels = { baixa: "Baixa", media: "Media", alta: "Alta", urgente: "Urgente" };
const priorityColors = { baixa: "bg-green-100 text-green-800", media: "bg-yellow-100 text-yellow-800", alta: "bg-orange-100 text-orange-800", urgente: "bg-red-100 text-red-800" };
const stageStatusColors = { pendente: "bg-slate-200 text-slate-700", em_andamento: "bg-blue-200 text-blue-800", concluido: "bg-green-200 text-green-800" };
const matReqStatusLabels = { pendente: "Pendente", parcial: "Parcial", confirmado: "Confirmado", devolvido: "Devolvido" };
const matReqStatusColors = { pendente: "bg-slate-200 text-slate-700", parcial: "bg-yellow-200 text-yellow-800", confirmado: "bg-green-200 text-green-800", devolvido: "bg-orange-200 text-orange-800" };

export default function OrdersPage() {
  const { can } = useAuth();
  const isAdmin = can("ordens", "edit");
  const canCreate = can("ordens", "create");
  const canDelete = can("ordens", "delete");
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [form, setForm] = useState({
    client_id: "", title: "", description: "", priority: "media",
    deadline: null, start_date: null, quantity: 1, stages: [{ name: "", sub_stages: [] }], order_code: ""
  });
  const [editForm, setEditForm] = useState({ title: "", description: "", priority: "media", order_code: "", deadline: null, start_date: null, quantity: 1, client_id: "", product_id: "" });
  const [editingStage, setEditingStage] = useState(null);
  const [editStageName, setEditStageName] = useState("");
  const [addStageOpen, setAddStageOpen] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [expandedStages, setExpandedStages] = useState({});
  const [addSubOpen, setAddSubOpen] = useState(null);
  const [subForm, setSubForm] = useState({ name: "", quantity: 1, unit: "peca" });
  const [editingSubId, setEditingSubId] = useState(null);
  const [editSubForm, setEditSubForm] = useState({ name: "", quantity: 1, unit: "peca", start_date: "", deadline: "" });
  const [matSearchOpen, setMatSearchOpen] = useState(null);
  const [matSearch, setMatSearch] = useState("");
  const [matQty, setMatQty] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [defaultStages, setDefaultStages] = useState([]);
  const [configUnits, setConfigUnits] = useState([]);
  const filters = useFilters();
  const [editingMatId, setEditingMatId] = useState(null);
  const [editMatQty, setEditMatQty] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(data);
    } catch { toast.error("Erro ao carregar ordens"); }
    finally { setLoading(false); }
  }, []);

  const fetchClients = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/clients`, { withCredentials: true }); setClients(data); } catch (err) { console.error("fetchClients:", err); }
  }, []);

  const fetchMaterials = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/materials`, { withCredentials: true }); setMaterials(data); } catch (err) { console.error("fetchMaterials:", err); }
  }, []);

  useEffect(() => {
    fetchOrders(); fetchClients(); fetchMaterials();
    axios.get(`${API}/products`, { withCredentials: true })
      .then(({ data }) => setProducts(data)).catch((err) => console.error("fetchProducts:", err));
    // Load default stages from settings
    axios.get(`${API}/settings/default_stages`, { withCredentials: true })
      .then(({ data }) => { if (data.items?.length) setDefaultStages(data.items); })
      .catch(() => {});
    axios.get(`${API}/settings/units`, { withCredentials: true })
      .then(({ data }) => { if (data.items?.length) setConfigUnits(data.items); })
      .catch(() => {});
  }, [fetchOrders, fetchClients, fetchMaterials]);

  // Map product stage name to the matching default stage label
  const mapStageName = (rawName) => {
    if (!rawName) return rawName;
    const byValue = defaultStages.find((ds) => ds.value === rawName);
    if (byValue) return byValue.label;
    const byLabel = defaultStages.find((ds) => ds.label.toLowerCase() === rawName.toLowerCase());
    if (byLabel) return byLabel.label;
    return rawName;
  };

  const applyProductTemplate = (productId) => {
    if (!productId) return;
    const product = products.find((p) => p.id === productId);
    if (!product) return;
    setForm((prev) => {
      const qty = prev.quantity || 1;
      const templateStages = (product.stages || []).map((s) => ({
        name: mapStageName(s.name),
        start_date: "",
        deadline: "",
        sub_stages: (s.sub_stages || []).map((ss) => ({
          name: ss.name,
          quantity: (ss.quantity || 1) * qty,
          unit: ss.unit || "unidade",
          start_date: "", deadline: ""
        })),
        materials: (s.materials || []).map((m) => ({
          material_id: m.material_id,
          planned_quantity: (m.planned_quantity || 1) * qty
        }))
      }));
      return {
        ...prev,
        title: prev.title || product.name,
        stages: templateStages.length ? templateStages : prev.stages,
        _productId: productId,
      };
    });
    toast.success(`Template aplicado com quantidades multiplicadas por ${form.quantity || 1}`);
  };

  const handleOrderClientChange = (clientId) => {
    setForm((prev) => ({
      ...prev,
      client_id: clientId,
      _productId: undefined,
      stages: prev._productId ? [{ name: "", start_date: "", deadline: "", sub_stages: [] }] : prev.stages,
    }));
  };

  const handleQuantityChange = (newQty) => {
    const qty = parseInt(newQty) || 1;
    if (form._productId) {
      const product = products.find((p) => p.id === form._productId);
      if (product) {
        const templateStages = (product.stages || []).map((s) => ({
          name: mapStageName(s.name),
          start_date: "",
          deadline: "",
          sub_stages: (s.sub_stages || []).map((ss) => ({
            name: ss.name,
            quantity: (ss.quantity || 1) * qty,
            unit: ss.unit || "unidade",
            start_date: "", deadline: ""
          })),
          materials: (s.materials || []).map((m) => ({
            material_id: m.material_id,
            planned_quantity: (m.planned_quantity || 1) * qty
          }))
        }));
        setForm((prev) => ({ ...prev, quantity: qty, stages: templateStages }));
        return;
      }
    }
    setForm((prev) => ({ ...prev, quantity: qty }));
  };

  const openCreate = () => {
    setForm({ client_id: "", title: "", description: "", priority: "media", deadline: null, start_date: null, quantity: 1, stages: [{ name: "", start_date: "", deadline: "", sub_stages: [] }], order_code: "" });
    setCreateOpen(true);
  };

  const handleDuplicateOrder = (order) => {
    setForm({
      client_id: order.client_id || "",
      title: order.title || "",
      description: order.description || "",
      priority: order.priority || "media",
      deadline: null,
      start_date: null,
      quantity: order.quantity || 1,
      order_code: "",
      stages: (order.stages || []).map((s) => ({
        name: s.name,
        start_date: s.start_date || "",
        deadline: s.deadline || "",
        sub_stages: (s.sub_stages || []).map((ss) => ({
          name: ss.name, quantity: ss.quantity || 1, unit: ss.unit || "peca",
          start_date: "", deadline: ""
        })),
        materials: (s.materials || []).map((m) => ({
          material_id: m.material_id, planned_quantity: m.planned_quantity
        }))
      }))
    });
    setDetailOpen(false);
    setCreateOpen(true);
    toast.info("Ordem duplicada - altere os dados e salve");
  };

  const openDetail = async (order) => {
    try {
      const { data } = await axios.get(`${API}/orders/${order.id}`, { withCredentials: true });
      setSelectedOrder(data);
      // Auto-expand all stages
      const expanded = {};
      (data.stages || []).forEach((s) => { expanded[s.id] = true; });
      setExpandedStages(expanded);
      setDetailOpen(true);
    } catch { toast.error("Erro ao carregar ordem"); }
  };

  // Stage helpers
  const addStage = () => setForm({ ...form, stages: [...form.stages, { name: "", start_date: "", deadline: "", sub_stages: [] }] });
  const removeStage = (idx) => setForm({ ...form, stages: form.stages.filter((_, i) => i !== idx) });
  const updateStageName = (idx, name) => {
    const s = [...form.stages]; s[idx] = { ...s[idx], name }; setForm({ ...form, stages: s });
  };
  const updateStageDate = (idx, field, value) => {
    const s = [...form.stages];
    s[idx] = { ...s[idx], [field]: value };
    // Cascade to all sub-stages
    if (s[idx].sub_stages?.length > 0) {
      s[idx].sub_stages = s[idx].sub_stages.map((ss) => ({ ...ss, [field]: value }));
    }
    setForm({ ...form, stages: s });
  };
  const addSubStageToForm = (stageIdx) => {
    const s = [...form.stages];
    const stage = s[stageIdx];
    const subs = [...(stage.sub_stages || []), { name: "", quantity: 1, unit: "peca", start_date: stage.start_date || "", deadline: stage.deadline || "" }];
    s[stageIdx] = { ...s[stageIdx], sub_stages: subs };
    setForm({ ...form, stages: s });
  };
  const removeSubStageFromForm = (stageIdx, subIdx) => {
    const s = [...form.stages];
    s[stageIdx] = { ...s[stageIdx], sub_stages: s[stageIdx].sub_stages.filter((_, i) => i !== subIdx) };
    setForm({ ...form, stages: s });
  };
  const updateSubStageInForm = (stageIdx, subIdx, field, value) => {
    const s = [...form.stages];
    const subs = [...s[stageIdx].sub_stages];
    subs[subIdx] = { ...subs[subIdx], [field]: value };
    s[stageIdx] = { ...s[stageIdx], sub_stages: subs };
    setForm({ ...form, stages: s });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const validStages = form.stages.filter((s) => s.name.trim());
    if (!validStages.length) { toast.error("Adicione pelo menos uma etapa"); return; }
    try {
      const payload = {
        client_id: form.client_id, title: form.title, description: form.description,
        order_code: form.order_code,
        priority: form.priority, deadline: form.deadline ? form.deadline.toISOString() : "",
        start_date: form.start_date ? form.start_date.toISOString() : "",
        quantity: form.quantity || 1,
        product_id: form._productId || "",
        stages: validStages.map((s) => ({
          name: s.name.trim(),
          start_date: s.start_date || "",
          deadline: s.deadline || "",
          materials: (s.materials || []).filter((m) => m.material_id && m.planned_quantity > 0).map((m) => ({ material_id: m.material_id, planned_quantity: m.planned_quantity })),
          sub_stages: (s.sub_stages || []).filter((ss) => ss.name.trim()).map((ss) => ({
            name: ss.name.trim(), quantity: ss.quantity || 1, unit: ss.unit || "peca",
            start_date: ss.start_date || "",
            deadline: ss.deadline || ""
          }))
        })),
      };
      const { data } = await axios.post(`${API}/orders`, payload, { withCredentials: true });
      if (data.stock_warnings?.length) {
        data.stock_warnings.forEach((w) => toast.warning(`Estoque baixo: ${w}`));
      }
      toast.success("Ordem criada com sucesso");
      setCreateOpen(false);
      fetchOrders();
      fetchMaterials();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar ordem"); }
  };

  const updateStageStatus = async (orderId, stageId, newStatus) => {
    try {
      const { data } = await axios.put(`${API}/orders/${orderId}/stages/${stageId}`, { status: newStatus }, { withCredentials: true });
      setSelectedOrder(data);
      fetchOrders();
      toast.success("Etapa atualizada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar etapa"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedOrder) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/orders/${selectedOrder.id}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await axios.get(`${API}/orders/${selectedOrder.id}`, { withCredentials: true });
      setSelectedOrder(data);
      toast.success("Arquivo enviado");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar arquivo"); }
    finally { setUploading(false); }
  };

  const downloadFile = async (fileRef) => {
    try {
      const response = await axios.get(`${API}/orders/${selectedOrder.id}/files/${fileRef.id}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a"); a.href = url; a.download = fileRef.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao baixar arquivo"); }
  };

  const filtered = filters.filterOrders(
    filterStatus === "all" ? orders : orders.filter((o) => o.status === filterStatus)
  );

  const availableProducts = form.client_id ? products.filter((p) => p.client_id === form.client_id) : [];

  const getMaterialInfo = (matId) => materials.find((m) => m.id === matId);

  const openEditOrder = (order) => {
    setEditForm({
      title: order.title || "",
      description: order.description || "",
      priority: order.priority || "media",
      order_code: order.order_code || "",
      deadline: order.deadline ? new Date(order.deadline) : null,
      start_date: order.start_date ? new Date(order.start_date) : null,
      quantity: order.quantity || 1,
      client_id: order.client_id || "",
      product_id: order.product_id || "",
    });
    setEditOpen(true);
  };

  const handleEditOrder = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        priority: editForm.priority,
        order_code: editForm.order_code,
        deadline: editForm.deadline ? editForm.deadline.toISOString() : "",
        start_date: editForm.start_date ? editForm.start_date.toISOString() : "",
        quantity: editForm.quantity || 1,
        client_id: editForm.client_id,
        product_id: editForm.product_id || "",
      };
      const { data } = await axios.put(`${API}/orders/${selectedOrder.id}`, payload, { withCredentials: true });
      setSelectedOrder(data);
      setEditOpen(false);
      fetchOrders();
      toast.success("Ordem atualizada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar ordem"); }
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;
    try {
      await axios.delete(`${API}/orders/${selectedOrder.id}`, { withCredentials: true });
      toast.success("Ordem excluida");
      setDeleteConfirmOpen(false);
      setDetailOpen(false);
      setSelectedOrder(null);
      fetchOrders();
      fetchMaterials();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao excluir ordem"); }
  };

  const handlePrintOrder = async () => {
    if (!selectedOrder) return;
    const o = selectedOrder;
    const prioLabel = priorityLabels[o.priority] || o.priority;
    const statusLabel = statusLabels[o.status] || o.status;
    const deadline = o.deadline ? format(new Date(o.deadline), "dd/MM/yyyy") : "Sem prazo";
    const startDate = o.start_date ? format(new Date(o.start_date), "dd/MM/yyyy") : "Nao definida";
    const createdAt = format(new Date(o.created_at), "dd/MM/yyyy");

    // Collect all materials from all stages for print
    const allPrintMats = (o.stages || []).flatMap((s) =>
      (s.materials || []).map((m) => ({ ...m, stage_name: s.name }))
    );
    const matsTableHtml = allPrintMats.length > 0 ? allPrintMats.map((m) => {
      const mStatus = m.status === "confirmado" ? "Confirmado" : m.status === "parcial" ? "Parcial" : "Pendente";
      return `<tr><td>${m.material_code ? m.material_code + " - " : ""}${m.material_name}</td><td>${m.planned_quantity} ${m.unit}</td><td>${m.confirmed_quantity} ${m.unit}</td><td>${mStatus}</td><td>${m.stage_name}</td></tr>`;
    }).join("") : '<tr><td colspan="5" style="text-align:center;color:#888">Nenhum material empenhado</td></tr>';

    const stagesHtml = (o.stages || []).map((s, idx) => {
      const sLabel = statusLabels[s.status] || s.status;
      const subsHtml = (s.sub_stages || []).map((ss) => {
        const ssLabel = statusLabels[ss.status] || ss.status;
        const statusClass = ss.status === "concluido" ? "done" : ss.status === "em_andamento" ? "progress" : "pending";
        const ssStart = ss.start_date ? format(new Date(ss.start_date), "dd/MM/yy") : "-";
        const ssDead = ss.deadline ? format(new Date(ss.deadline), "dd/MM/yy") : "-";
        return `<tr class="sub-row"><td style="padding-left:40px">${ss.name}</td><td>${ss.quantity} ${ss.unit}</td><td>${ssStart} - ${ssDead}</td><td><span class="status ${statusClass}">${ssLabel}</span></td></tr>`;
      }).join("");
      const statusClass = s.status === "concluido" ? "done" : s.status === "em_andamento" ? "progress" : "pending";
      const count = s.sub_stages?.length ? ` (${s.sub_stages.filter(ss => ss.status === "concluido").length}/${s.sub_stages.length})` : "";
      return `<tr class="stage-row"><td><strong>${idx + 1}. ${s.name}</strong></td><td>${count}</td><td></td><td><span class="status ${statusClass}">${sLabel}</span></td></tr>${subsHtml}`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ordem ${o.order_code || o.title}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; color: #222; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #333; padding-bottom: 12px; margin-bottom: 16px; }
      .header h1 { font-size: 18px; margin-bottom: 4px; }
      .header .code { font-size: 14px; color: #555; font-family: monospace; }
      .header .right { text-align: right; font-size: 11px; color: #555; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr; gap: 12px; margin-bottom: 16px; padding: 10px; background: #f7f7f7; border-radius: 4px; }
      .info-grid .label { font-size: 9px; text-transform: uppercase; color: #888; letter-spacing: 0.5px; }
      .info-grid .value { font-size: 12px; font-weight: bold; margin-top: 2px; }
      .desc { margin-bottom: 16px; padding: 8px; background: #fafafa; border-left: 3px solid #ddd; }
      h2 { font-size: 14px; margin: 16px 0 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      th { background: #f0f0f0; text-align: left; padding: 6px 8px; font-size: 10px; text-transform: uppercase; border-bottom: 2px solid #ddd; }
      td { padding: 6px 8px; border-bottom: 1px solid #eee; }
      .stage-row td { background: #fafafa; }
      .sub-row td { font-size: 11px; }
      .status { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: bold; }
      .status.done { background: #d4edda; color: #155724; }
      .status.progress { background: #cce5ff; color: #004085; }
      .status.pending { background: #f0f0f0; color: #666; }
      .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 10px; color: #888; text-align: center; }
      .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 32px; page-break-inside: avoid; }
      .sig-box { border: 1.5px solid #333; border-radius: 4px; padding: 12px; }
      .sig-box h3 { font-size: 12px; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #ccc; padding-bottom: 6px; margin-bottom: 10px; }
      .sig-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .sig-field { margin-bottom: 8px; }
      .sig-field .sig-label { font-size: 9px; text-transform: uppercase; color: #888; margin-bottom: 2px; }
      .sig-field .sig-line { border-bottom: 1px solid #333; height: 24px; }
      .sig-field .sig-line-tall { border-bottom: 1px solid #333; height: 36px; }
      @media print { body { padding: 10px; } .no-print { display: none; } }
    </style></head><body>
    <div class="header">
      <div>
        <h1>${o.title}</h1>
        ${o.order_code ? `<div class="code">${o.order_code}</div>` : ""}
      </div>
      <div class="right">
        <div>Liga Facil - PCP</div>
        <div>Impresso em: ${format(new Date(), "dd/MM/yyyy HH:mm")}</div>
      </div>
    </div>
    <div class="info-grid">
      <div><div class="label">Cliente</div><div class="value">${o.client_name}</div></div>
      <div><div class="label">Quantidade</div><div class="value">${o.quantity || 1}</div></div>
      <div><div class="label">Prioridade</div><div class="value">${prioLabel}</div></div>
      <div><div class="label">Data de Inicio</div><div class="value">${startDate}</div></div>
      <div><div class="label">Prazo de Entrega</div><div class="value">${deadline}</div></div>
      <div><div class="label">Status</div><div class="value">${statusLabel}</div></div>
    </div>
    <div class="info-grid" style="grid-template-columns:1fr 1fr">
      <div><div class="label">Criado por</div><div class="value">${o.created_by}</div></div>
      <div><div class="label">Criado em</div><div class="value">${createdAt}</div></div>
    </div>
    ${o.description ? `<div class="desc"><strong>Descricao:</strong> ${o.description}</div>` : ""}
    <h2>Materiais Empenhados</h2>
    <table><thead><tr><th>Material</th><th>Planejado</th><th>Confirmado</th><th>Status</th><th>Etapa</th></tr></thead><tbody>${matsTableHtml}</tbody></table>
    <h2>Etapas do Processo</h2>
    <table><thead><tr><th>Etapa</th><th>Progresso</th><th>Datas</th><th>Status</th></tr></thead><tbody>${stagesHtml}</tbody></table>
    <div class="signatures">
      <div class="sig-box">
        <h3>Qualidade</h3>
        <div class="sig-fields">
          <div class="sig-field"><div class="sig-label">Data</div><div class="sig-line"></div></div>
          <div class="sig-field"><div class="sig-label">Hora</div><div class="sig-line"></div></div>
        </div>
        <div class="sig-field" style="margin-top:8px"><div class="sig-label">Assinatura / Aprovacao</div><div class="sig-line-tall"></div></div>
      </div>
      <div class="sig-box">
        <h3>Expedicao</h3>
        <div class="sig-fields">
          <div class="sig-field"><div class="sig-label">Data</div><div class="sig-line"></div></div>
          <div class="sig-field"><div class="sig-label">Hora</div><div class="sig-line"></div></div>
        </div>
        <div class="sig-fields" style="margin-top:8px">
          <div class="sig-field"><div class="sig-label">Peso (kg)</div><div class="sig-line"></div></div>
          <div class="sig-field"><div class="sig-label">Cubagem (m3)</div><div class="sig-line"></div></div>
        </div>
        <div class="sig-field"><div class="sig-label">Volume</div><div class="sig-line"></div></div>
        <div class="sig-field" style="margin-top:8px"><div class="sig-label">Assinatura / Aprovacao</div><div class="sig-line-tall"></div></div>
      </div>
    </div>
    <div class="footer">Liga Facil - Sistema de Planejamento e Controle de Producao</div>
    `;

    // Fetch attached files - convert PDFs to page images via backend
    // Includes order-specific files AND drawings attached to the linked product.
    let filesHtml = "";
    const allPages = [];
    const files = o.files || [];
    for (const f of files) {
      try {
        const { data } = await axios.get(`${API}/orders/${o.id}/files/${f.id}/pages`, { withCredentials: true });
        for (const pg of (data.pages || [])) {
          allPages.push({ name: f.original_filename, page: pg.page, totalPages: data.total_pages, dataUrl: pg.data_url });
        }
      } catch (err) { console.error("Error fetching file pages:", f.original_filename, err); }
    }
    // Product drawings (printed automatically with every order of this product)
    if (o.product_id) {
      try {
        const { data: prod } = await axios.get(`${API}/products/${o.product_id}`, { withCredentials: true });
        for (const f of (prod.files || [])) {
          try {
            const { data } = await axios.get(`${API}/products/${o.product_id}/files/${f.id}/pages`, { withCredentials: true });
            for (const pg of (data.pages || [])) {
              allPages.push({ name: f.original_filename, page: pg.page, totalPages: data.total_pages, dataUrl: pg.data_url });
            }
          } catch (err) { console.error("Error fetching product file pages:", f.original_filename, err); }
        }
      } catch (err) { console.error("Error fetching product for drawings:", err); }
    }
    if (allPages.length > 0) {
      // Cada desenho em sua propria pagina. O cabecalho fica junto ao 1o desenho
      // (sem div vazio) para nao gerar pagina em branco antes dos desenhos.
      filesHtml = "";
      allPages.forEach((pg, i) => {
        const label = pg.totalPages > 1 ? `${pg.name} - Pagina ${pg.page}/${pg.totalPages}` : pg.name;
        const heading = i === 0 ? `<h2 style="font-size:14px;border-bottom:1px solid #ddd;padding-bottom:4px;margin:0 0 8px">Desenhos Tecnicos</h2>` : "";
        filesHtml += `<div style="page-break-before:always;page-break-inside:avoid">${heading}<p style="font-size:11px;color:#555;margin-bottom:8px">${label}</p><img src="${pg.dataUrl}" style="max-width:100%;display:block;border:1px solid #ddd;border-radius:4px" /></div>`;
      });
    }

    const finalHtml = html + filesHtml + `<script>window.onload=function(){window.print()}</script></body></html>`;
    const blob = new Blob([finalHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank");
    if (printWindow) printWindow.onload = () => URL.revokeObjectURL(url);
  };

  // Stage management (admin)
  const handleEditStage = async (stageId) => {
    if (!selectedOrder || !editStageName.trim()) return;
    try {
      const { data } = await axios.patch(`${API}/orders/${selectedOrder.id}/stages/${stageId}`, { name: editStageName.trim() }, { withCredentials: true });
      setSelectedOrder(data);
      setEditingStage(null);
      fetchOrders();
      toast.success("Etapa renomeada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao editar etapa"); }
  };

  const handleUpdateStageDates = async (stageId, field, value) => {
    if (!selectedOrder) return;
    try {
      const payload = { [field]: value };
      const { data } = await axios.patch(`${API}/orders/${selectedOrder.id}/stages/${stageId}`, payload, { withCredentials: true });
      setSelectedOrder(data);
      fetchOrders();
      toast.success("Datas da etapa atualizadas (propagadas para sub-etapas)");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar datas"); }
  };

  const handleDeleteStage = async (stageId, stageName) => {
    if (!selectedOrder) return;
    if (!window.confirm(`Confirma exclusao da etapa "${stageName}"?`)) return;
    try {
      const { data } = await axios.delete(`${API}/orders/${selectedOrder.id}/stages/${stageId}`, { withCredentials: true });
      setSelectedOrder(data);
      fetchOrders();
      toast.success("Etapa removida");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover etapa"); }
  };

  const handleAddStage = async () => {
    if (!selectedOrder || !newStageName.trim()) return;
    try {
      const { data } = await axios.post(`${API}/orders/${selectedOrder.id}/stages`, { name: newStageName.trim() }, { withCredentials: true });
      setSelectedOrder(data);
      setNewStageName("");
      setAddStageOpen(false);
      fetchOrders();
      toast.success("Etapa adicionada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao adicionar etapa"); }
  };

  const toggleStageExpand = (stageId) => {
    setExpandedStages((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
  };

  // Sub-stage CRUD
  const handleAddSubStage = async (stageId) => {
    if (!selectedOrder || !subForm.name.trim()) return;
    try {
      const { data } = await axios.post(`${API}/orders/${selectedOrder.id}/stages/${stageId}/sub_stages`, subForm, { withCredentials: true });
      setSelectedOrder(data);
      setSubForm({ name: "", quantity: 1, unit: "peca" });
      setAddSubOpen(null);
      fetchOrders();
      toast.success("Sub-etapa adicionada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao adicionar sub-etapa"); }
  };

  const handleEditSubStage = async (stageId, subId) => {
    if (!selectedOrder) return;
    try {
      const payload = {};
      if (editSubForm.name) payload.name = editSubForm.name;
      if (editSubForm.quantity >= 0) payload.quantity = editSubForm.quantity;
      if (editSubForm.unit) payload.unit = editSubForm.unit;
      payload.start_date = editSubForm.start_date || "";
      payload.deadline = editSubForm.deadline || "";
      const { data } = await axios.patch(`${API}/orders/${selectedOrder.id}/stages/${stageId}/sub_stages/${subId}`, payload, { withCredentials: true });
      setSelectedOrder(data);
      setEditingSubId(null);
      fetchOrders();
      toast.success("Sub-etapa atualizada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao editar sub-etapa"); }
  };

  const handleDeleteSubStage = async (stageId, subId, subName) => {
    if (!selectedOrder) return;
    if (!window.confirm(`Confirma exclusao da sub-etapa "${subName}"?`)) return;
    try {
      const { data } = await axios.delete(`${API}/orders/${selectedOrder.id}/stages/${stageId}/sub_stages/${subId}`, { withCredentials: true });
      setSelectedOrder(data);
      fetchOrders();
      toast.success("Sub-etapa removida");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover sub-etapa"); }
  };

  const handleSubStageStatus = async (stageId, subId, status) => {
    if (!selectedOrder) return;
    try {
      const { data } = await axios.put(`${API}/orders/${selectedOrder.id}/stages/${stageId}/sub_stages/${subId}`, { status }, { withCredentials: true });
      setSelectedOrder(data);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar sub-etapa"); }
  };

  const filteredMaterials = materials.filter((m) => {
    const q = matSearch.toLowerCase();
    const matchesSearch = m.name.toLowerCase().includes(q) || (m.code || "").toLowerCase().includes(q);
    if (!matchesSearch) return false;
    // Strict filter: if order has a client, show ONLY materials of that client
    if (selectedOrder?.client_id) {
      return m.client_id === selectedOrder.client_id;
    }
    return true;
  });

  const handleAddStageMaterial = async (stageId, materialId) => {
    if (!selectedOrder || !materialId || matQty <= 0) return;
    try {
      const { data } = await axios.post(`${API}/orders/${selectedOrder.id}/stages/${stageId}/materials`,
        { material_id: materialId, planned_quantity: matQty }, { withCredentials: true });
      setSelectedOrder(data);
      if (data.stock_warning) toast.warning(data.stock_warning);
      else toast.success("Material adicionado a etapa");
      setMatSearchOpen(null);
      setMatSearch("");
      setMatQty(1);
      fetchOrders();
      fetchMaterials();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao adicionar material"); }
  };

  const handleRemoveStageMaterial = async (stageId, reqId, matName) => {
    if (!selectedOrder) return;
    if (!window.confirm(`Remover "${matName}" desta etapa?`)) return;
    try {
      const { data } = await axios.delete(`${API}/orders/${selectedOrder.id}/stages/${stageId}/materials/${reqId}`, { withCredentials: true });
      setSelectedOrder(data);
      fetchOrders();
      toast.success("Material removido");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover material"); }
  };

  const handleEditStageMaterial = async (stageId, reqId) => {
    if (!selectedOrder || editMatQty <= 0) return;
    try {
      const { data } = await axios.put(`${API}/orders/${selectedOrder.id}/stages/${stageId}/materials/${reqId}`,
        { planned_quantity: editMatQty }, { withCredentials: true });
      setSelectedOrder(data);
      setEditingMatId(null);
      fetchOrders();
      toast.success("Quantidade atualizada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao editar material"); }
  };

  const [confirmMatId, setConfirmMatId] = useState(null);
  const [confirmMatQty, setConfirmMatQty] = useState(0);
  const [confirmMatInfo, setConfirmMatInfo] = useState(null);

  const openConfirmDialog = (mr) => {
    const remaining = mr.planned_quantity - (mr.confirmed_quantity || 0);
    setConfirmMatId(mr.id);
    setConfirmMatQty(remaining);
    setConfirmMatInfo(mr);
  };

  const handleConfirmMaterial = async () => {
    if (!selectedOrder || !confirmMatId) return;
    if (!confirmMatQty || confirmMatQty <= 0) { toast.error("Informe uma quantidade valida"); return; }
    try {
      const { data } = await axios.post(`${API}/orders/${selectedOrder.id}/materials/${confirmMatId}/confirm`,
        { quantity: confirmMatQty }, { withCredentials: true });
      setSelectedOrder(data);
      fetchOrders();
      fetchMaterials();
      toast.success("Material confirmado - estoque atualizado");
      setConfirmMatId(null);
      setConfirmMatInfo(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao confirmar"); }
  };

  const handleReturnMaterial = async (reqId) => {
    if (!selectedOrder) return;
    if (!window.confirm("Devolver material ao estoque?")) return;
    try {
      const { data } = await axios.post(`${API}/orders/${selectedOrder.id}/materials/${reqId}/return`, {}, { withCredentials: true });
      setSelectedOrder(data);
      fetchOrders();
      fetchMaterials();
      toast.success("Material devolvido ao estoque");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao devolver"); }
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="orders-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Ordens de Producao</h1>
          <p className="text-sm text-muted-foreground mt-1">Gerencie as ordens de producao</p>
        </div>
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="order-status-filter"><SelectValue placeholder="Filtrar status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="em_andamento">Em Andamento</SelectItem>
              <SelectItem value="concluido">Concluido</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          {canCreate && (
          <Button onClick={openCreate} data-testid="create-order-btn">
            <Plus className="h-4 w-4 mr-2" /> Nova Ordem
          </Button>
          )}
        </div>
      </div>

      <FilterBar filters={filters} clients={clients} showPriorityFilter={true} />

      {/* Orders Table */}
      <Card className="border border-border shadow-none overflow-hidden">
        <CardContent className="p-0">
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[100px]">Codigo</TableHead>
                  <TableHead className="min-w-[250px]">Titulo</TableHead>
                  <TableHead className="min-w-[160px]">Cliente</TableHead>
                  <TableHead className="min-w-[110px]">Qtd. Produzindo</TableHead>
                  <TableHead className="min-w-[120px]">Status</TableHead>
                  <TableHead className="min-w-[90px]">Prioridade</TableHead>
                  <TableHead className="min-w-[100px]">Prazo de Entrega</TableHead>
                  <TableHead className="min-w-[80px]">Etapas</TableHead>
                  <TableHead className="min-w-[80px]">Materiais</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Nenhuma ordem encontrada</TableCell></TableRow>
                ) : (
                  filtered.map((order) => {
                    const totalStages = order.stages?.length || 0;
                    const doneStages = order.stages?.filter((s) => s.status === "concluido").length || 0;
                    const allStageMats = (order.stages || []).flatMap((s) => s.materials || []);
                    const totalMats = allStageMats.length;
                    const confirmedMats = allStageMats.filter((m) => m.status === "confirmado").length;
                    const isLate = order.deadline && new Date(order.deadline) < new Date() && !["concluido", "cancelado"].includes(order.status);
                    return (
                      <TableRow key={order.id} className="cursor-pointer" onClick={() => openDetail(order)} data-testid={`order-row-${order.id}`}>
                        <TableCell className="font-mono text-xs">{order.order_code || "-"}</TableCell>
                        <TableCell className="font-medium"><span className="line-clamp-2 text-sm">{order.title}</span></TableCell>
                        <TableCell><span className="line-clamp-1 text-sm">{order.client_name}</span></TableCell>
                        <TableCell><span className="text-sm font-semibold tabular-nums" data-testid={`order-quantity-${order.id}`}>{order.quantity ?? 1}</span></TableCell>
                        <TableCell>
                          <Badge className={statusColors[order.status]}>{statusLabels[order.status]}</Badge>
                          {isLate && <Badge className="ml-1 bg-red-100 text-red-800">Atrasado</Badge>}
                        </TableCell>
                        <TableCell><Badge className={priorityColors[order.priority]}>{priorityLabels[order.priority]}</Badge></TableCell>
                        <TableCell className="text-sm">{order.deadline ? format(new Date(order.deadline), "dd/MM/yyyy") : "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-14 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: totalStages ? `${(doneStages / totalStages) * 100}%` : "0%" }} />
                            </div>
                            <span className="text-xs text-muted-foreground">{doneStages}/{totalStages}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {totalMats > 0 ? (
                            <div className="flex items-center gap-1">
                              <Package className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">{confirmedMats}/{totalMats}</span>
                            </div>
                          ) : <span className="text-xs text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openDetail(order); }} data-testid={`view-order-${order.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile/Tablet Cards */}
          <div className="lg:hidden divide-y">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Nenhuma ordem encontrada</div>
            ) : (
              filtered.map((order) => {
                const totalStages = order.stages?.length || 0;
                const doneStages = order.stages?.filter((s) => s.status === "concluido").length || 0;
                const allStageMats = (order.stages || []).flatMap((s) => s.materials || []);
                const totalMats = allStageMats.length;
                const confirmedMats = allStageMats.filter((m) => m.status === "confirmado").length;
                const isLate = order.deadline && new Date(order.deadline) < new Date() && !["concluido", "cancelado"].includes(order.status);
                return (
                  <div key={order.id} className="p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => openDetail(order)} data-testid={`order-row-${order.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {order.order_code && <span className="font-mono text-xs font-semibold text-primary shrink-0">{order.order_code}</span>}
                          <Badge className={statusColors[order.status] + " text-[10px]"}>{statusLabels[order.status]}</Badge>
                          {isLate && <Badge className="bg-red-100 text-red-800 text-[10px]">Atrasado</Badge>}
                        </div>
                        <p className="font-medium text-sm mt-1">{order.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{order.client_name}</p>
                      </div>
                      <Eye className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: totalStages ? `${(doneStages / totalStages) * 100}%` : "0%" }} />
                        </div>
                        <span>{doneStages}/{totalStages} etapas</span>
                      </div>
                      {totalMats > 0 && <span><Package className="h-3 w-3 inline mr-0.5" />{confirmedMats}/{totalMats} mat</span>}
                      <span className="font-semibold text-foreground">Qtd: {order.quantity ?? 1}</span>
                      <Badge className={priorityColors[order.priority] + " text-[10px]"}>{priorityLabels[order.priority]}</Badge>
                      {order.deadline && <span>{format(new Date(order.deadline), "dd/MM/yyyy")}</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Ordem de Producao</DialogTitle>
            <DialogDescription>Defina os detalhes, etapas e materiais</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5">
            {/* Client Selector (choose first) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente * <span className="text-xs font-normal text-muted-foreground">(selecione primeiro)</span></Label>
                <Select value={form.client_id} onValueChange={handleOrderClientChange}>
                  <SelectTrigger data-testid="order-client-select"><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Codigo da Ordem</Label>
                <Input value={form.order_code} onChange={(e) => setForm({ ...form, order_code: e.target.value })} placeholder="Ex: OP-001" data-testid="order-code-input" />
              </div>
            </div>
            {/* Product Template Selector (filtered by selected client) */}
            <div className="p-3 border rounded-lg bg-muted/30 space-y-2">
              <Label className="font-semibold text-sm">Usar Template de Produto</Label>
              <Select value={form._productId || ""} onValueChange={applyProductTemplate} disabled={!form.client_id}>
                <SelectTrigger data-testid="order-product-select">
                  <SelectValue placeholder={form.client_id ? "Selecionar produto (opcional)" : "Selecione um cliente primeiro"} />
                </SelectTrigger>
                <SelectContent>
                  {availableProducts.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-muted-foreground text-center" data-testid="no-products-for-client">Nenhum produto vinculado a este cliente</div>
                  ) : availableProducts.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} - ` : ""}{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {form.client_id ? "Etapas e materiais serao preenchidos e multiplicados pela quantidade a produzir" : "Escolha o cliente acima para liberar os produtos deste cliente"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titulo *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="order-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Quantidade a Produzir</Label>
                <Input type="number" min={1} value={form.quantity} onChange={(e) => handleQuantityChange(e.target.value)} data-testid="order-quantity-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descricao</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="order-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="order-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data de Inicio</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="order-start-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.start_date ? format(form.start_date, "dd/MM/yyyy") : "Selecionar"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={form.start_date} onSelect={(d) => setForm({ ...form, start_date: d })} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Prazo de Entrega</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="order-deadline-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.deadline ? format(form.deadline, "dd/MM/yyyy") : "Selecionar"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={form.deadline} onSelect={(d) => setForm({ ...form, deadline: d })} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Stages */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Etapas do Processo *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addStage} data-testid="add-stage-btn">
                  <Plus className="h-3 w-3 mr-1" /> Etapa
                </Button>
              </div>
              <div className="space-y-3">
                {form.stages.map((stage, idx) => (
                  <div key={idx} className="rounded border border-border">
                    <div className="flex gap-2 items-center p-2">
                      <span className="text-xs text-muted-foreground w-6 shrink-0">{idx + 1}.</span>
                      <Select value={stage.name} onValueChange={(v) => updateStageName(idx, v)}>
                        <SelectTrigger data-testid={`stage-name-input-${idx}`}><SelectValue placeholder="Selecione a etapa" /></SelectTrigger>
                        <SelectContent>
                          {defaultStages.map((ds) => (
                            <SelectItem key={ds.value} value={ds.label}>{ds.label}</SelectItem>
                          ))}
                          {stage.name && !defaultStages.some((ds) => ds.label === stage.name) && (
                            <SelectItem key={`custom-${idx}`} value={stage.name}>{stage.name}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="sm" className="shrink-0 text-xs" onClick={() => addSubStageToForm(idx)} data-testid={`add-sub-to-stage-${idx}`}>
                        <Plus className="h-3 w-3 mr-1" /> Sub
                      </Button>
                      {form.stages.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive" onClick={() => removeStage(idx)}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    {/* Stage dates - cascade to sub-stages */}
                    <div className="flex gap-2 items-center px-2 pb-2 pl-10">
                      <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-[10px] text-muted-foreground shrink-0">Inicio:</span>
                      <Input type="date" value={stage.start_date ? stage.start_date.substring(0, 10) : ""}
                        onChange={(e) => updateStageDate(idx, "start_date", e.target.value ? e.target.value + "T12:00:00Z" : "")}
                        className="h-7 text-xs w-[130px]" data-testid={`stage-start-date-${idx}`} />
                      <span className="text-[10px] text-muted-foreground shrink-0">Entrega:</span>
                      <Input type="date" value={stage.deadline ? stage.deadline.substring(0, 10) : ""}
                        onChange={(e) => updateStageDate(idx, "deadline", e.target.value ? e.target.value + "T12:00:00Z" : "")}
                        className="h-7 text-xs w-[130px]" data-testid={`stage-deadline-${idx}`} />
                      <span className="text-[10px] text-muted-foreground italic">Propaga para sub-etapas</span>
                    </div>
                    {stage.sub_stages?.length > 0 && (
                      <div className="border-t border-border bg-muted/30 p-2 space-y-1.5">
                        {stage.sub_stages.map((sub, subIdx) => (
                          <div key={subIdx} className="flex gap-2 items-center pl-8 flex-wrap" data-testid={`create-sub-${idx}-${subIdx}`}>
                            <Input value={sub.name} onChange={(e) => updateSubStageInForm(idx, subIdx, "name", e.target.value)}
                              placeholder="Nome da sub-etapa" className="h-8 text-xs flex-1 min-w-[120px]" data-testid={`create-sub-name-${idx}-${subIdx}`} />
                            <Input type="number" value={sub.quantity} onChange={(e) => updateSubStageInForm(idx, subIdx, "quantity", parseFloat(e.target.value) || 0)}
                              className="h-8 text-xs w-16" placeholder="Qtd" data-testid={`create-sub-qty-${idx}-${subIdx}`} />
                            <Select value={sub.unit} onValueChange={(v) => updateSubStageInForm(idx, subIdx, "unit", v)}>
                              <SelectTrigger className="h-8 text-xs w-24" data-testid={`create-sub-unit-${idx}-${subIdx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {configUnits.length > 0 ? configUnits.map((u) => (
                                  <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                                )) : (
                                  <>
                                    <SelectItem value="peca">Peca</SelectItem>
                                    <SelectItem value="metro">Metro</SelectItem>
                                    <SelectItem value="unidade">Unidade</SelectItem>
                                    <SelectItem value="kg">Kg</SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            <Input type="date" value={sub.start_date ? sub.start_date.substring(0, 10) : ""} onChange={(e) => updateSubStageInForm(idx, subIdx, "start_date", e.target.value ? e.target.value + "T12:00:00Z" : "")}
                              className="h-8 text-xs w-[110px]" title="Inicio" data-testid={`create-sub-start-${idx}-${subIdx}`} />
                            <Input type="date" value={sub.deadline ? sub.deadline.substring(0, 10) : ""} onChange={(e) => updateSubStageInForm(idx, subIdx, "deadline", e.target.value ? e.target.value + "T12:00:00Z" : "")}
                              className="h-8 text-xs w-[110px]" title="Entrega" data-testid={`create-sub-deadline-${idx}-${subIdx}`} />
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-destructive" onClick={() => removeSubStageFromForm(idx, subIdx)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" className="w-full h-7 text-xs text-muted-foreground border border-dashed border-border/60 ml-8 mr-0"
                          style={{ width: "calc(100% - 32px)" }}
                          onClick={() => addSubStageToForm(idx)} data-testid={`add-more-sub-${idx}`}>
                          <Plus className="h-3 w-3 mr-1" /> Mais sub-etapa
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={!form.client_id || !form.title} data-testid="order-submit-btn">Criar Ordem</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Order Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={(v) => { setDetailOpen(v); if (!v) { setIsFullscreen(false); setEditingMatId(null); } }}>
        <DialogContent className={`${isFullscreen ? "max-w-[98vw] w-[98vw] h-[96vh] max-h-[96vh]" : "max-w-3xl max-h-[90vh]"} overflow-y-auto transition-all duration-200`}>
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedOrder.order_code && <span className="font-mono text-sm text-muted-foreground">{selectedOrder.order_code}</span>}
                  {selectedOrder.title}
                  <Badge className={statusColors[selectedOrder.status]}>{statusLabels[selectedOrder.status]}</Badge>
                </DialogTitle>
                <DialogDescription>Cliente: {selectedOrder.client_name} | Criado por: {selectedOrder.created_by}</DialogDescription>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={handlePrintOrder} data-testid="print-order-btn">
                    <Printer className="h-3.5 w-3.5 mr-1" /> Imprimir
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setIsFullscreen(!isFullscreen)} data-testid="fullscreen-btn">
                    {isFullscreen ? <Minimize2 className="h-3.5 w-3.5 mr-1" /> : <Maximize2 className="h-3.5 w-3.5 mr-1" />}
                    {isFullscreen ? "Reduzir" : "Expandir"}
                  </Button>
                  {isAdmin && (
                    <>
                      {canCreate && (
                      <Button variant="outline" size="sm" onClick={() => handleDuplicateOrder(selectedOrder)} data-testid="duplicate-order-btn">
                        <Copy className="h-3.5 w-3.5 mr-1" /> Duplicar
                      </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openEditOrder(selectedOrder)} data-testid="edit-order-btn">
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                      </Button>
                      {canDelete && (
                      <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setDeleteConfirmOpen(true)} data-testid="delete-order-btn">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Excluir
                      </Button>
                      )}
                    </>
                  )}
                </div>
              </DialogHeader>

              <div className="space-y-6">
                {/* Info Grid */}
                <div className="grid grid-cols-5 gap-4 text-sm">
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Prioridade</span>
                    <div className="mt-1"><Badge className={priorityColors[selectedOrder.priority]}>{priorityLabels[selectedOrder.priority]}</Badge></div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Quantidade</span>
                    <p className="mt-1 font-medium text-lg" data-testid="order-detail-quantity">{selectedOrder.quantity || 1}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Data de Inicio</span>
                    <p className="mt-1 font-medium">{selectedOrder.start_date ? format(new Date(selectedOrder.start_date), "dd/MM/yyyy") : "Nao definida"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Prazo de Entrega</span>
                    <p className="mt-1 font-medium">{selectedOrder.deadline ? format(new Date(selectedOrder.deadline), "dd/MM/yyyy") : "Sem prazo"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Criado em</span>
                    <p className="mt-1 font-medium">{format(new Date(selectedOrder.created_at), "dd/MM/yyyy")}</p>
                  </div>
                </div>

                {selectedOrder.description && (
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Descricao</span>
                    <p className="mt-1 text-sm">{selectedOrder.description}</p>
                  </div>
                )}

                {/* Stages */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-semibold text-base">Etapas do Processo</h3>
                      {canCreate && (
                      <Button variant="outline" size="sm" onClick={() => { setNewStageName(""); setAddStageOpen(true); }} data-testid="add-stage-to-order-btn">
                        <Plus className="h-3 w-3 mr-1" /> Etapa
                      </Button>
                      )}
                  </div>
                  <div className="space-y-2">
                    {selectedOrder.stages?.map((stage, idx) => {
                      const isEditing = editingStage === stage.id;
                      const hasSubs = stage.sub_stages && stage.sub_stages.length > 0;
                      const isExpanded = expandedStages[stage.id];
                      const subsCount = stage.sub_stages?.length || 0;
                      const subsDone = stage.sub_stages?.filter((ss) => ss.status === "concluido").length || 0;
                      return (
                        <div key={stage.id} className="rounded border border-border" data-testid={`detail-stage-${stage.id}`}>
                          {/* Stage header */}
                          <div className="flex items-center justify-between p-3 bg-card rounded-t">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <button type="button" onClick={() => toggleStageExpand(stage.id)} className="flex items-center gap-1 shrink-0">
                                <span className="text-xs text-muted-foreground font-mono w-5">{idx + 1}</span>
                                <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                              </button>
                              {isEditing ? (
                                <div className="flex items-center gap-2 flex-1">
                                  <Input value={editStageName} onChange={(e) => setEditStageName(e.target.value)} className="h-7 text-sm" autoFocus
                                    onKeyDown={(e) => { if (e.key === "Enter") handleEditStage(stage.id); if (e.key === "Escape") setEditingStage(null); }}
                                    data-testid={`edit-stage-name-input-${stage.id}`} />
                                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEditStage(stage.id)} data-testid={`save-stage-name-${stage.id}`}>
                                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingStage(null)}>
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <span className="text-sm font-medium truncate">{stage.name}</span>
                                  <Badge className={stageStatusColors[stage.status]}>{statusLabels[stage.status] || stage.status}</Badge>
                                  {hasSubs && <span className="text-[10px] text-muted-foreground">{subsDone}/{subsCount}</span>}
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {isAdmin && !isEditing && (
                                <>
                                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setEditingStage(stage.id); setEditStageName(stage.name); }} data-testid={`edit-stage-btn-${stage.id}`}>
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  {canDelete && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteStage(stage.id, stage.name); }} data-testid={`delete-stage-btn-${stage.id}`}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                  )}
                                </>
                              )}
                              {!isEditing && !hasSubs && (
                                <Select value={stage.status} onValueChange={(v) => updateStageStatus(selectedOrder.id, stage.id, v)}>
                                  <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`stage-status-select-${stage.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pendente">Pendente</SelectItem>
                                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                                    <SelectItem value="concluido">Concluido</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>

                          {/* Stage dates row */}
                          {isExpanded && (
                            <div className="flex items-center gap-3 px-3 py-2 border-t border-border bg-muted/10 flex-wrap">
                              <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-muted-foreground">Inicio:</span>
                                {isAdmin ? (
                                  <Input type="date" value={stage.start_date ? stage.start_date.substring(0, 10) : ""}
                                    onChange={(e) => handleUpdateStageDates(stage.id, "start_date", e.target.value ? e.target.value + "T12:00:00Z" : "")}
                                    className="h-7 text-xs w-[130px]" data-testid={`detail-stage-start-${stage.id}`} />
                                ) : (
                                  <span className="text-xs font-medium">{stage.start_date ? format(new Date(stage.start_date), "dd/MM/yy") : "-"}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-muted-foreground">Entrega:</span>
                                {isAdmin ? (
                                  <Input type="date" value={stage.deadline ? stage.deadline.substring(0, 10) : ""}
                                    onChange={(e) => handleUpdateStageDates(stage.id, "deadline", e.target.value ? e.target.value + "T12:00:00Z" : "")}
                                    className="h-7 text-xs w-[130px]" data-testid={`detail-stage-deadline-${stage.id}`} />
                                ) : (
                                  <span className="text-xs font-medium">{stage.deadline ? format(new Date(stage.deadline), "dd/MM/yy") : "-"}</span>
                                )}
                              </div>
                              {isAdmin && <span className="text-[10px] text-muted-foreground italic">Altera todas sub-etapas</span>}
                            </div>
                          )}

                          {/* Sub-stages (expanded) */}
                          {isExpanded && (
                            <div className="border-t border-border bg-muted/30 p-2 space-y-1.5 rounded-b">
                              {stage.sub_stages?.map((sub) => {
                                const isEditingSub = editingSubId === sub.id;
                                return (
                                  <div key={sub.id} className="flex items-center justify-between px-3 py-2 rounded bg-card border border-border/60 text-sm" data-testid={`sub-stage-${sub.id}`}>
                                    {isEditingSub ? (
                                      <div className="flex items-center gap-2 flex-1 flex-wrap">
                                        <Input value={editSubForm.name} onChange={(e) => setEditSubForm({ ...editSubForm, name: e.target.value })} className="h-7 text-xs flex-1 min-w-[120px]" autoFocus
                                          onKeyDown={(e) => { if (e.key === "Enter") handleEditSubStage(stage.id, sub.id); if (e.key === "Escape") setEditingSubId(null); }}
                                          data-testid={`edit-sub-name-${sub.id}`} />
                                        <Input type="number" value={editSubForm.quantity} onChange={(e) => setEditSubForm({ ...editSubForm, quantity: parseFloat(e.target.value) || 0 })} className="h-7 text-xs w-16" data-testid={`edit-sub-qty-${sub.id}`} />
                                        <Input value={editSubForm.unit} onChange={(e) => setEditSubForm({ ...editSubForm, unit: e.target.value })} className="h-7 text-xs w-20" data-testid={`edit-sub-unit-${sub.id}`} />
                                        <Input type="date" value={editSubForm.start_date ? editSubForm.start_date.substring(0, 10) : ""} onChange={(e) => setEditSubForm({ ...editSubForm, start_date: e.target.value ? e.target.value + "T12:00:00Z" : "" })} className="h-7 text-xs w-[110px]" title="Data Inicio" data-testid={`edit-sub-start-${sub.id}`} />
                                        <Input type="date" value={editSubForm.deadline ? editSubForm.deadline.substring(0, 10) : ""} onChange={(e) => setEditSubForm({ ...editSubForm, deadline: e.target.value ? e.target.value + "T12:00:00Z" : "" })} className="h-7 text-xs w-[110px]" title="Data Entrega" data-testid={`edit-sub-deadline-${sub.id}`} />
                                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEditSubStage(stage.id, sub.id)}>
                                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                        </Button>
                                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingSubId(null)}>
                                          <X className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <>
                                        <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                                          <span className="truncate">{sub.name}</span>
                                          <span className="text-xs text-muted-foreground font-mono shrink-0">{sub.quantity} {sub.unit}</span>
                                          {sub.start_date && <span className="text-[10px] text-muted-foreground shrink-0">Inicio: {format(new Date(sub.start_date), "dd/MM/yy")}</span>}
                                          {sub.deadline && <span className="text-[10px] text-muted-foreground shrink-0">Entrega: {format(new Date(sub.deadline), "dd/MM/yy")}</span>}
                                          <Badge className={`text-[10px] ${stageStatusColors[sub.status]}`}>{statusLabels[sub.status] || sub.status}</Badge>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                          {isAdmin && (
                                            <>
                                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setEditingSubId(sub.id); setEditSubForm({ name: sub.name, quantity: sub.quantity, unit: sub.unit, start_date: sub.start_date || "", deadline: sub.deadline || "" }); }} data-testid={`edit-sub-btn-${sub.id}`}>
                                                <Pencil className="h-2.5 w-2.5" />
                                              </Button>
                                              {canDelete && (
                                              <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => handleDeleteSubStage(stage.id, sub.id, sub.name)} data-testid={`delete-sub-btn-${sub.id}`}>
                                                <Trash2 className="h-2.5 w-2.5" />
                                              </Button>
                                              )}
                                            </>
                                          )}
                                          <Select value={sub.status} onValueChange={(v) => handleSubStageStatus(stage.id, sub.id, v)}>
                                            <SelectTrigger className="w-[120px] h-7 text-xs" data-testid={`sub-status-select-${sub.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="pendente">Pendente</SelectItem>
                                              <SelectItem value="em_andamento">Em Andamento</SelectItem>
                                              <SelectItem value="concluido">Concluido</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                );
                              })}

                              {/* Add sub-stage */}
                              {canCreate && addSubOpen === stage.id ? (
                                <div className="flex items-center gap-2 px-3 py-2 rounded border border-dashed border-border">
                                  <Input value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} placeholder="Nome da sub-etapa" className="h-7 text-xs flex-1"
                                    autoFocus onKeyDown={(e) => { if (e.key === "Enter") handleAddSubStage(stage.id); if (e.key === "Escape") setAddSubOpen(null); }}
                                    data-testid="new-sub-name-input" />
                                  <Input type="number" value={subForm.quantity} onChange={(e) => setSubForm({ ...subForm, quantity: parseFloat(e.target.value) || 0 })} className="h-7 text-xs w-16" placeholder="Qtd" data-testid="new-sub-qty-input" />
                                  <Input value={subForm.unit} onChange={(e) => setSubForm({ ...subForm, unit: e.target.value })} className="h-7 text-xs w-20" placeholder="Un." data-testid="new-sub-unit-input" />
                                  <Button size="sm" className="h-7 text-xs" onClick={() => handleAddSubStage(stage.id)} disabled={!subForm.name.trim()} data-testid="confirm-add-sub-btn">OK</Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAddSubOpen(null)}>X</Button>
                                </div>
                              ) : canCreate ? (
                                <Button variant="ghost" size="sm" className="w-full h-7 text-xs text-muted-foreground border border-dashed border-border/60"
                                  onClick={() => { setSubForm({ name: "", quantity: 1, unit: "peca" }); setAddSubOpen(stage.id); }} data-testid={`add-sub-btn-${stage.id}`}>
                                  <Plus className="h-3 w-3 mr-1" /> Adicionar sub-etapa
                                </Button>
                              ) : null}

                              {(!stage.sub_stages || stage.sub_stages.length === 0) && !isAdmin && (
                                <p className="text-xs text-muted-foreground text-center py-2">Nenhuma sub-etapa</p>
                              )}

                              {/* Materials per stage */}
                              <div className="border-t border-border/50 pt-2 mt-2">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
                                    <Package className="h-3 w-3" /> Materiais
                                    {stage.materials?.length > 0 && <span className="ml-1">({stage.materials.length})</span>}
                                  </span>
                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2"
                                    onClick={() => { setMatSearchOpen(matSearchOpen === stage.id ? null : stage.id); setMatSearch(""); setMatQty(1); }}
                                    data-testid={`add-mat-btn-${stage.id}`}>
                                    <Plus className="h-2.5 w-2.5 mr-0.5" /> Material
                                  </Button>
                                </div>

                                {/* Existing materials */}
                                {stage.materials?.map((mr) => {
                                  const statusColor = mr.status === "confirmado" ? "bg-green-100 text-green-800" : mr.status === "parcial" ? "bg-blue-100 text-blue-800" : mr.status === "devolvido" ? "bg-gray-100 text-gray-600" : "bg-yellow-50 text-yellow-800";
                                  const isEditing = editingMatId === mr.id;
                                  return (
                                    <div key={mr.id} className="flex items-center justify-between px-2 py-1.5 rounded bg-card border border-border/60 mb-1 text-xs" data-testid={`stage-mat-${mr.id}`}>
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {mr.material_code && <span className="font-mono text-muted-foreground">{mr.material_code}</span>}
                                        <span className="truncate">{mr.material_name}</span>
                                        {isEditing ? (
                                          <div className="flex items-center gap-1">
                                            <Input type="number" value={editMatQty} onChange={(e) => setEditMatQty(parseFloat(e.target.value) || 0)}
                                              className="h-6 text-xs w-20" min={mr.confirmed_quantity || 0} step="0.01" data-testid={`edit-mat-qty-${mr.id}`} />
                                            <span className="text-muted-foreground">{mr.unit}</span>
                                            <Button size="sm" className="h-6 text-[10px] px-2" onClick={() => handleEditStageMaterial(stage.id, mr.id)} data-testid={`save-edit-mat-${mr.id}`}>OK</Button>
                                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1" onClick={() => setEditingMatId(null)}>X</Button>
                                          </div>
                                        ) : (
                                          <>
                                            <span className="text-muted-foreground">{mr.planned_quantity} {mr.unit}</span>
                                            {mr.confirmed_quantity > 0 && <span className="text-green-700">({mr.confirmed_quantity} conf.)</span>}
                                          </>
                                        )}
                                        <Badge className={`text-[9px] ${statusColor}`}>{mr.status === "confirmado" ? "Confirmado" : mr.status === "parcial" ? "Parcial" : mr.status === "devolvido" ? "Devolvido" : "Pendente"}</Badge>
                                      </div>
                                      <div className="flex items-center gap-0.5 shrink-0">
                                        {mr.status !== "confirmado" && mr.status !== "devolvido" && !isEditing && (
                                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setEditingMatId(mr.id); setEditMatQty(mr.planned_quantity); }} title="Editar quantidade" data-testid={`edit-mat-${mr.id}`}>
                                            <Pencil className="h-2.5 w-2.5 text-blue-600" />
                                          </Button>
                                        )}
                                        {mr.status !== "confirmado" && mr.status !== "devolvido" && (
                                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => openConfirmDialog(mr)} title="Confirmar baixa" data-testid={`confirm-mat-${mr.id}`}>
                                            <CheckCircle2 className="h-3 w-3 text-green-600" />
                                          </Button>
                                        )}
                                        {mr.confirmed_quantity > 0 && mr.status !== "devolvido" && (
                                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleReturnMaterial(mr.id)} title="Devolver ao estoque" data-testid={`return-mat-${mr.id}`}>
                                            <RotateCcw className="h-3 w-3 text-orange-500" />
                                          </Button>
                                        )}
                                        {mr.status !== "confirmado" && (
                                          <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => handleRemoveStageMaterial(stage.id, mr.id, mr.material_name)} title="Excluir material" data-testid={`remove-mat-${mr.id}`}>
                                            <Trash2 className="h-2.5 w-2.5" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Add material search */}
                                {matSearchOpen === stage.id && (
                                  <div className="p-2 rounded border border-dashed border-border space-y-1.5">
                                    <Input value={matSearch} onChange={(e) => setMatSearch(e.target.value)}
                                      placeholder="Buscar por codigo ou nome do material..."
                                      className="h-7 text-xs" autoFocus data-testid={`mat-search-input-${stage.id}`} />
                                    {matSearch.trim() && (
                                      <div className="max-h-32 overflow-y-auto space-y-0.5">
                                        {filteredMaterials.length === 0 ? (
                                          <p className="text-[10px] text-muted-foreground text-center py-1">Nenhum material encontrado</p>
                                        ) : filteredMaterials.slice(0, 8).map((m) => (
                                          <button key={m.id} type="button" className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-xs flex items-center justify-between"
                                            onClick={() => handleAddStageMaterial(stage.id, m.id)}
                                            data-testid={`mat-option-${m.id}`}>
                                            <span className="flex items-center gap-1.5 min-w-0">
                                              {m.code && <span className="font-mono text-muted-foreground shrink-0">{m.code}</span>}
                                              <span className="truncate">{m.name}</span>
                                              {m.client_name && <span className="text-[9px] text-blue-600 bg-blue-50 px-1 rounded shrink-0">{m.client_name}</span>}
                                            </span>
                                            <span className="text-muted-foreground shrink-0 ml-2">{m.quantity} {m.unit}</span>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] text-muted-foreground">Qtd:</span>
                                      <Input type="number" value={matQty} onChange={(e) => setMatQty(parseFloat(e.target.value) || 0)}
                                        className="h-6 text-xs w-20" min="0.01" step="0.01" data-testid={`mat-qty-input-${stage.id}`} />
                                      <Button size="sm" variant="ghost" className="h-6 text-xs ml-auto" onClick={() => setMatSearchOpen(null)}>Fechar</Button>
                                    </div>
                                  </div>
                                )}

                                {(!stage.materials || stage.materials.length === 0) && matSearchOpen !== stage.id && (
                                  <p className="text-[10px] text-muted-foreground text-center py-1">Nenhum material</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Add Stage inline */}
                  {addStageOpen && (
                    <div className="flex items-center gap-2 mt-2 p-3 rounded border border-dashed border-border">
                      <Select value={newStageName} onValueChange={setNewStageName}>
                        <SelectTrigger className="h-8 text-sm" data-testid="new-stage-name-input"><SelectValue placeholder="Selecione a etapa" /></SelectTrigger>
                        <SelectContent>
                          {defaultStages.map((ds) => (
                            <SelectItem key={ds.value} value={ds.label}>{ds.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={handleAddStage} disabled={!newStageName.trim()} data-testid="confirm-add-stage-btn">Adicionar</Button>
                      <Button size="sm" variant="ghost" onClick={() => setAddStageOpen(false)}>Cancelar</Button>
                    </div>
                  )}
                </div>

                {/* Files */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-semibold text-base">Arquivos / Desenhos</h3>
                    <label className="cursor-pointer">
                      <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="file-upload-input" />
                      <Button variant="outline" size="sm" asChild disabled={uploading}>
                        <span><Upload className="h-3 w-3 mr-1" />{uploading ? "Enviando..." : "Upload"}</span>
                      </Button>
                    </label>
                  </div>
                  {selectedOrder.files?.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhum arquivo anexado</p>
                  ) : (
                    <div className="space-y-1">
                      {selectedOrder.files?.map((f) => (
                        <div key={f.id} className="flex items-center justify-between p-2 rounded border border-border text-sm hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => downloadFile(f)} data-testid={`file-${f.id}`}>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-primary" />
                            <span>{f.original_filename}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{f.uploaded_by}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog (Admin only) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Ordem</DialogTitle>
            <DialogDescription>Altere os dados da ordem de producao</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditOrder} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Codigo da Ordem</Label>
                <Input value={editForm.order_code} onChange={(e) => setEditForm({ ...editForm, order_code: e.target.value })} placeholder="Ex: OP-001" data-testid="edit-order-code-input" />
              </div>
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={editForm.client_id} onValueChange={(v) => setEditForm({ ...editForm, client_id: v })}>
                  <SelectTrigger data-testid="edit-order-client-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titulo *</Label>
                <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} required data-testid="edit-order-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Quantidade a Produzir</Label>
                <Input type="number" min={1} value={editForm.quantity} onChange={(e) => setEditForm({ ...editForm, quantity: parseInt(e.target.value) || 1 })} data-testid="edit-order-quantity-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Produto Vinculado</Label>
              <Select value={editForm.product_id || "none"} onValueChange={(v) => setEditForm({ ...editForm, product_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="edit-order-product-select"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} - ` : ""}{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">Vincula dados do produto (desenho, preco, cubagem) na expedicao</p>
            </div>
            <div className="space-y-2">
              <Label>Descricao</Label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={2} data-testid="edit-order-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                  <SelectTrigger data-testid="edit-order-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data de Inicio</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="edit-order-start-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editForm.start_date ? format(editForm.start_date, "dd/MM/yyyy") : "Selecionar"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={editForm.start_date} onSelect={(d) => setEditForm({ ...editForm, start_date: d })} />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Prazo de Entrega</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="edit-order-deadline-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editForm.deadline ? format(editForm.deadline, "dd/MM/yyyy") : "Selecionar"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={editForm.deadline} onSelect={(d) => setEditForm({ ...editForm, deadline: d })} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="edit-order-submit-btn">Salvar Alteracoes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Order Confirm Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusao</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir a ordem <span className="font-semibold text-foreground">{selectedOrder?.order_code || selectedOrder?.title}</span>?
              {selectedOrder?.stages?.some((s) => s.materials?.some((m) => m.confirmed_quantity > 0)) &&
                <span className="block mt-1 text-amber-600">Materiais confirmados serao devolvidos ao estoque automaticamente.</span>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteOrder} data-testid="confirm-delete-order-btn">Excluir Ordem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Material Dialog */}
      <Dialog open={!!confirmMatId} onOpenChange={(open) => { if (!open) { setConfirmMatId(null); setConfirmMatInfo(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar Baixa de Material</DialogTitle>
            <DialogDescription>
              {confirmMatInfo && (
                <>
                  <span className="font-medium text-foreground">{confirmMatInfo.material_code ? confirmMatInfo.material_code + " - " : ""}{confirmMatInfo.material_name}</span>
                  <br />
                  Planejado: {confirmMatInfo.planned_quantity} {confirmMatInfo.unit}
                  {confirmMatInfo.confirmed_quantity > 0 && <> | Ja confirmado: {confirmMatInfo.confirmed_quantity}</>}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Quantidade a confirmar (baixar do estoque)</Label>
              <Input type="number" step="0.01" min={0.01} value={confirmMatQty}
                onChange={(e) => setConfirmMatQty(parseFloat(e.target.value) || 0)}
                data-testid="confirm-mat-qty-input" autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") handleConfirmMaterial(); }} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setConfirmMatId(null); setConfirmMatInfo(null); }}>Cancelar</Button>
              <Button onClick={handleConfirmMaterial} data-testid="confirm-mat-submit-btn">Confirmar Baixa</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
